// Turning a rejected `POST /v1/boats/:boatId/alerts` (custom branch) into
// something the builder can put NEXT TO THE FIELD THAT CAUSED IT (custom +
// conditional alarms, phase 5).
//
// The API's messages are written for an API client — they name wire keys
// (`setDelayMs`), prefix the mode (`cruise: …`), and describe the v1 single-block
// rule in one long sentence. Showing those verbatim to an operator is jargon, so
// this module maps each documented failure onto (a) the draft field it belongs
// to and (b) plain English.
//
// The mapping is deliberately keyed on the error CODE plus a marker in the
// message, not on the message text as a whole: the codes are the contract
// (`invalid_input`, `invalid_thresholds`, `invalid_conditions`,
// `unsupported_on_boat`), and anything unrecognised falls through to the form's
// own error slot rather than being silently dropped.
//
// Phase 6 adds the condition rows. A 422 names ONE path, which may be the
// trigger's pin or a clause's, so the caller passes a {@link CreateErrorContext}
// describing the submitted draft and the rejection lands on the row that pinned
// that path.

import { RECIPE_WITHDRAWN_ERROR } from '@neomaris/rules';

import { conditionField, type CustomAlarmDraft, type CustomAlarmField } from './customAlarmForm';

/** A server rejection, placed and translated for the builder. */
export interface CreateErrorView {
  /** The draft field to show it beside; absent ⇒ the form-level error slot. */
  readonly field?: CustomAlarmField;
  readonly message: string;
  /**
   * True for the 422: the boat stopped reporting a path the draft pinned — the
   * trigger's or a condition's — while the form was open, so the catalog on
   * screen is out of date and worth refetching.
   */
  readonly staleCatalog?: boolean;
}

/**
 * What the SUBMITTED draft pinned, so a 422 naming a path can be placed on the
 * row that pinned it. The API reports one path per rejection and checks the
 * trigger pin before the clauses, so trigger-vs-clause is never ambiguous.
 * Optional throughout: without it a path-shaped 422 falls back to the trigger's
 * field, which is where it always landed before conditions existed.
 */
export interface CreateErrorContext {
  /** The custom pin (`draft.signalPath`). */
  readonly signalPath?: string;
  /** Each condition row's path, in row order (`undefined` for an unfilled row). */
  readonly conditionPaths?: readonly (string | undefined)[];
}

/** {@link CreateErrorContext} for a draft — what the builder passes at the call site. */
export function errorContextFor(draft: CustomAlarmDraft): CreateErrorContext {
  return {
    signalPath: draft.signalPath,
    conditionPaths: draft.conditions.map((c) => c.signalPath),
  };
}

/** The error `api()` throws: an HTTP status plus the parsed JSON body. */
interface ApiErrorLike {
  readonly status?: number;
  readonly body?: unknown;
}

interface ErrorBody {
  readonly error?: string;
  readonly message?: string;
  readonly path?: string;
}

function bodyOf(err: unknown): ErrorBody {
  const body = (err as ApiErrorLike | null)?.body;
  if (body === null || typeof body !== 'object') return {};
  const b = body as Record<string, unknown>;
  return {
    error: typeof b.error === 'string' ? b.error : undefined,
    message: typeof b.message === 'string' ? b.message : undefined,
    path: typeof b.path === 'string' ? b.path : undefined,
  };
}

const GENERIC = 'Something went wrong saving this alarm. Please try again.';
/** The withdrawn-recipe line for when the server sent no sentence (TOM-136). */
export const WITHDRAWN_FALLBACK = 'This alarm is no longer offered: no boat can watch it.';

/**
 * Whether a failed request was refused because the recipe is withdrawn — the
 * ONE `recipe_withdrawn` predicate every surface uses (core ADR-218).
 */
export function isRecipeWithdrawnError(err: unknown): boolean {
  return (
    (err as ApiErrorLike | null)?.status === 422 && bodyOf(err).error === RECIPE_WITHDRAWN_ERROR
  );
}

/** What a withdrawn-recipe refusal says: the server's sentence, or the fallback. */
export function withdrawnErrorMessage(err: unknown): string {
  const message = bodyOf(err).message?.trim() ?? '';
  return message.length > 0 ? message : WITHDRAWN_FALLBACK;
}

/**
 * Where an `invalid_thresholds` rejection belongs. The clear-point message also
 * contains the words "set point", so it is tested FIRST — otherwise every
 * wrong-side clear point would land on the set-point field.
 */
function thresholdError(message: string): CreateErrorView {
  if (/clear point/i.test(message)) {
    return {
      field: 'clearpoint',
      message:
        'This alarm could never clear with that clear point — put it on the side the reading recovers to.',
    };
  }
  if (/set point/i.test(message)) {
    return { field: 'setpoint', message: 'Enter a set point.' };
  }
  if (/setDelayMs/.test(message)) {
    return { field: 'setDelaySeconds', message: 'A delay cannot be negative.' };
  }
  if (/clearDelayMs/.test(message)) {
    return { field: 'clearDelaySeconds', message: 'A delay cannot be negative.' };
  }
  if (/staleMs/.test(message)) {
    return { field: 'staleSeconds', message: 'The waiting time cannot be negative.' };
  }
  // A reserved-key typo or the v1 single-block rule: neither belongs to one
  // visible field, so it goes to the form slot rather than beside a guess.
  return { message: 'Those threshold values were not accepted. Check them and try again.' };
}

/**
 * Where an `invalid_conditions` rejection belongs: the "Only when…" section.
 * The API's two messages both name the offending PRESET, which a custom alarm
 * has no control over (the builder never offers it) — so the translation says
 * what to do instead rather than repeating the wire word.
 */
function conditionsError(message: string): CreateErrorView {
  if (/preset/i.test(message)) {
    return {
      field: 'conditions',
      message:
        'This alarm cannot use the engine-running shortcut. Add a condition on a signal instead.',
    };
  }
  return {
    field: 'conditions',
    message: 'Those conditions were not accepted. Check them and try again.',
  };
}

/**
 * The field a 422's path belongs to: the trigger's signal, or the condition row
 * that pinned it. The trigger wins a tie — the API pre-flights the custom pin
 * first, so a path used in both places is reported for the pin.
 */
function fieldForUnsupportedPath(
  path: string | undefined,
  context: CreateErrorContext | undefined
): CustomAlarmField {
  if (path === undefined || path.length === 0) return 'signalPath';
  if (context === undefined) return 'signalPath';
  if (context.signalPath === path) return 'signalPath';
  const index = context.conditionPaths?.findIndex((p) => p === path) ?? -1;
  return index >= 0 ? conditionField(index) : 'signalPath';
}

/**
 * Place and translate a create failure. Anything unrecognised — a 500, an
 * offline fetch, a code from a newer server — returns the generic message with
 * no field, which the builder shows in its form-level slot.
 *
 * `context` describes the draft that was submitted; it is what lets a 422 land
 * on the condition row that named the path rather than always on the trigger.
 */
export function mapCreateError(err: unknown, context?: CreateErrorContext): CreateErrorView {
  const status = (err as ApiErrorLike | null)?.status;
  const { error: code, message = '', path } = bodyOf(err);

  if (status === 422 && code === 'unsupported_on_boat') {
    const named = path === undefined || path.length === 0 ? 'that signal' : path;
    return {
      field: fieldForUnsupportedPath(path, context),
      staleCatalog: true,
      message: `The boat no longer reports ${named}. Pick a signal it is reporting now.`,
    };
  }

  // The recipe is withdrawn (core ADR-216, TOM-136): no boat can arm it. The
  // server's own sentence says so in the owner's words; nothing in the form can
  // fix it, so it lands in the form-level slot.
  if (isRecipeWithdrawnError(err)) return { message: withdrawnErrorMessage(err) };

  if (status === 400) {
    if (code === 'invalid_thresholds') return thresholdError(message);
    if (code === 'invalid_conditions') return conditionsError(message);
    if (code === 'invalid_input' && /title/i.test(message)) {
      return { field: 'title', message: 'Give this alarm a name.' };
    }
    if (code === 'invalid_input' && /custom\.path|path/i.test(message)) {
      return { field: 'signalPath', message: 'Pick the signal this alarm should watch.' };
    }
    return { message: 'That alarm could not be saved. Check the values and try again.' };
  }

  // A boat holds at most ONE active alarm per recipe, so the RECIPE branch can
  // reject a create the custom branch never can (a custom alarm gets a synthetic
  // per-definition recipe id and cannot collide). Reachable from Neo's proposal
  // card (ADR 0161), whose draft was validated when it was drafted — the owner
  // may have added the same alarm in another tab before clicking Create.
  if (status === 409 && code === 'duplicate_recipe') {
    return { message: 'This boat already has that alarm — no need to add it again.' };
  }

  if (status === 401) return { message: 'Your session has expired. Sign in again to save this.' };
  if (status === 403 || status === 404) {
    return { message: 'You do not have access to this boat.' };
  }

  return { message: GENERIC };
}

/**
 * The same placement and translation for an EDIT
 * (`PATCH /v1/boats/:boatId/alerts/:alertId`, phase 7). The write contract is
 * the create branch's — same codes, same messages, same 422 pre-flight — with
 * one difference worth its own sentence: a 404 here means the ALARM is gone (it
 * was removed from another tab or another device), not that the boat is out of
 * reach, and telling an operator they lack access to their own boat would send
 * them hunting for a permissions problem that does not exist.
 */
export function mapPatchError(err: unknown, context?: CreateErrorContext): CreateErrorView {
  const status = (err as ApiErrorLike | null)?.status;
  const { error: code } = bodyOf(err);
  if (status === 404 && code !== undefined && code !== 'unauthenticated') {
    return {
      message: 'This alarm is no longer on your boat — it may have been removed somewhere else.',
    };
  }
  return mapCreateError(err, context);
}
